import { useEffect, useMemo } from 'react'

import type { Member, Network } from '@tribeplatform/gql-client/types'

import {
  ActorAnalyticsContext,
  SnowplowTrackerFactory,
  TargetAnalyticsContext,
} from '../lib/snowplow'

function getLang() {
  if (typeof window === 'undefined') return
  if (Array.isArray(navigator?.languages)) return navigator.languages[0]
  return navigator?.language
}

const unsetContextValues = (
  context:
    | Partial<ActorAnalyticsContext>
    | Partial<TargetAnalyticsContext> = {},
) => {
  return Object.keys(context).reduce((acc, key: string) => {
    acc[key] = undefined
    return acc
  }, {})
}

interface UseTracker {
  actor?: Partial<ActorAnalyticsContext>
  target?: Partial<TargetAnalyticsContext>
  member: Member
  network: Network
}

export const useTracker = ({
  actor = {},
  target = {},
  member,
  network,
}: UseTracker) => {
  const { id: networkId, organizationId } = network || {}
  const targetDefaultContext: TargetAnalyticsContext = useMemo(
    () => ({
      organizationId: organizationId || '',
      networkId,
    }),
    [networkId, organizationId],
  )

  const actorDefaultContext: ActorAnalyticsContext = useMemo(
    () => ({
      id: member?.id || '',
      roleId: member?.role?.id || '',
      roleType: member?.role?.type || '',
      locale: member?.locale || getLang(),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [member?.id],
  )

  const tracker = SnowplowTrackerFactory('tribe-analytics', {})

  useEffect(() => {
    tracker.setActor({ ...actorDefaultContext, ...actor })
    tracker.setTarget({ ...targetDefaultContext, ...target })

    return () => {
      tracker.setActor(unsetContextValues(actor))
      tracker.setTarget(unsetContextValues(target))
    }
  }, [tracker, actor, target, actorDefaultContext, targetDefaultContext])

  return tracker
}
